import { MappingServiceProductMatchResult } from '@upright/common/model/input/companies/products';
import { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';
import { BlanketProductSetStatus } from '../../../blanket/blanket-product-set/types';

export interface PiuDataState {
  products: PiuProductState[];
  blanketStatus?: BlanketProductSetStatus;
  sync: boolean;
  /**
   * PLT-5109: true when the loaded product set is an *old* "blanketti" set — produced by neither
   * the product input UI nor the automatic product set generation (company mapping); i.e. its
   * `sourceMetadata.sourceId` is some legacy source (or absent). Such sets are rendered read-only:
   * products are visible, but the per-product edit modal and the matching/attributes view are
   * hidden, and the control buttons are disabled with a tooltip.
   */
  readOnly?: boolean;
  // initial: boolean;
}

/**
 * Product input UI product state
 */
export interface PiuProductState {
  id: string;
  /**
   * Parent product's `PiuProductState.id` (stringified BlanketProduct id, or
   * client-generated UUID for newly-added products), or `null` for top-level
   * products. Introduced in PLT-4748 to ship the PLG hierarchy data model.
   * Populated from `BlanketProduct.parentId` on read; in this PR the UI
   * surfaces it only as a read-only hierarchy view. The save handler
   * reconstructs the `children` payload tree from these references — there is
   * no compute-pipeline behavior change.
   */
  parentId?: string | null;
  /**
   * True for segment / container nodes — the grouping rows introduced in PLT-5690. They carry no
   * taxonomy mapping and get no mixin, and are persisted with `meta.isSegment` set; leaf products
   * have `isSegment` unset / false and round-trip through the mixin path. Kept as an explicit flag
   * (rather than inferring "has no children") so an emptied-out segment isn't mistaken for a leaf
   * on save, and so the backend can tell the two apart at publish (PLT-5899).
   */
  isSegment?: boolean;
  revenueShare?: number;
  title: string;
  description: string;
  ipr?: IPRAttributes;
  normalizedName?: string;
  manualIprEdits?: Partial<IPRAttributes>;
  productMatchResult: MappingServiceProductMatchResult | undefined;
  productMatchStatus?: ProductMatchStatus;
  countryHQ_IsoAlpha3?: string;
  // Manual product matching override fields
  autoMatchedProductCode?: string;
  isManuallyOverridden?: boolean;
  manualOverrideTimestamp?: string;
  /** Set on products the chat read from uploaded files (PLT-7013). */
  fileEvidence?: ProductFileEvidence;
}

/**
 * What the uploaded files say about one product, written by the chat when it builds a product set
 * from files (PLT-7013). Chat uploads are readable only in the turn they arrive, so this note is
 * the only trace of the files once that turn ends — it has to survive every later save of the row.
 */
export interface ProductFileEvidence {
  /** How and to whom the product is sold, what it is, and any sustainability claims, as the files state them. */
  note: string;
  /** File name and page(s) the product was read from, e.g. "Acme deck.pdf, p. 4". */
  source?: string;
  /** How the row's revenue weight was set, e.g. "segment 'Services' (42%), split equally across 3 products". */
  weightSource: string;
  /**
   * PLT-7015: the row's `ipr` was set by the chat from the files, not by the matcher. Matching then
   * only re-runs the taxonomy match and keeps the attributes, wherever the row is matched (both
   * editors and publish). Kept here rather than as a field of its own because `fileEvidence` already
   * survives every save path of the row.
   */
  attributesFromFiles?: true;
}

export enum ProductMatchStatus {
  PENDING = 'p',
  RUNNING = 'r',
  SUCCESS = 's',
  ERROR = 'e',
}

export interface MatchProductToUpgPayload {
  companyName: string | undefined;
  companyOverview: string | undefined;
  companyWebsite: string | undefined;
  title: string;
  description: string;
  /**
   * PLT-7015: the company the row belongs to, so the server can tell whether its product set was
   * built from files. Optional: a caller without one gets today's matching.
   */
  companyId?: string;
  /** PLT-7015: the row carries attributes set from files (`hasAttributesFromFiles`); keep them. */
  attributesFromFiles?: boolean;
}

export interface MatchProductToUpgResponse {
  /** Absent when the row's own attributes are kept (`attributesFromFiles`); the caller keeps its `ipr`. */
  ipr?: IPRAttributes;
  productMatch: MappingServiceProductMatchResult;
}
